import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ArrowsRightLeftIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import {
  caseRelationLabel,
  caseTransferredFrom,
  caseTransferredTo,
  siblingOpenCasesForHeader,
} from "./caseSiblingUtils";

const SIBLING_PREVIEW_LIMIT = 5;

function RelatedCaseLink({ row }) {
  if (!row?.id) return null;
  return (
    <Link to={`/dashboard/cases/${row.id}`} className="font-semibold text-[#0B3A78] underline">
      {caseRelationLabel(row)}
    </Link>
  );
}

function TransferOutBody({ to, t }) {
  return (
    <p className="text-sm text-slate-700">
      {t("case.transfer.banner_out")} <RelatedCaseLink row={to} />
    </p>
  );
}

function TransferInBody({ from, t }) {
  return (
    <ul className="space-y-2 text-left text-sm text-slate-700">
      {from.map((src) => (
        <li key={src.id}>
          {t("case.transfer.banner_in")} <RelatedCaseLink row={src} />
        </li>
      ))}
    </ul>
  );
}

function SiblingOpenBody({ list, t }) {
  return (
    <div className="space-y-2 text-left text-sm text-slate-700">
      <p className="text-slate-600">{t("case.siblings.hint")}</p>
      <ul className="max-h-64 space-y-1 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-2">
        {list.slice(0, SIBLING_PREVIEW_LIMIT).map((row) => (
          <li key={row.id}>
            <Link
              to={`/dashboard/cases/${row.id}`}
              className="flex items-start justify-between gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-[#0B3A78] hover:bg-white"
            >
              <span>
                {caseRelationLabel(row)}
                {row.status ? (
                  <span className="mt-0.5 block text-xs font-medium text-slate-500">
                    {row.status}
                  </span>
                ) : null}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {list.length > SIBLING_PREVIEW_LIMIT ? (
        <p className="text-xs text-slate-500">
          {t("case.siblings.more", { count: list.length - SIBLING_PREVIEW_LIMIT })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Compact header tags for transfer links and other open cases on the same customer.
 * Full details open in a PMDialog — no tall banners in the case header.
 */
export function CaseHeaderRelationTags({ caseItem, siblingCases }) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState(null);

  const to = caseTransferredTo(caseItem);
  const from = useMemo(() => caseTransferredFrom(caseItem), [caseItem]);
  const siblings = useMemo(
    () => siblingOpenCasesForHeader(siblingCases, caseItem?.id, caseItem?.status),
    [siblingCases, caseItem?.id, caseItem?.status]
  );

  const hasOut = Boolean(to);
  const hasIn = from.length > 0;
  const hasSiblings = siblings.length > 0;

  if (!hasOut && !hasIn && !hasSiblings) return null;

  const dialogTitle =
    dialog === "siblings"
      ? t("case.siblings.title", { count: siblings.length })
      : dialog === "in"
      ? t("case.transfer.banner_in")
      : t("case.transfer.banner_out");

  return (
    <>
      <div className="cv-relation-tags" role="group" aria-label={t("case.relations.tags_label")}>
        {hasOut ? (
          <button
            type="button"
            className="cv-relation-tag is-out"
            aria-haspopup="dialog"
            aria-expanded={dialog === "out"}
            onClick={() => setDialog("out")}
          >
            <ArrowsRightLeftIcon className="cv-relation-tag-icon" aria-hidden="true" />
            {t("case.transfer.tag_out")}
          </button>
        ) : null}
        {hasIn ? (
          <button
            type="button"
            className="cv-relation-tag is-in"
            aria-haspopup="dialog"
            aria-expanded={dialog === "in"}
            onClick={() => setDialog("in")}
          >
            <ArrowsRightLeftIcon className="cv-relation-tag-icon" aria-hidden="true" />
            {from.length > 1
              ? t("case.transfer.tag_in_count", { count: from.length })
              : t("case.transfer.tag_in")}
          </button>
        ) : null}
        {hasSiblings ? (
          <button
            type="button"
            className="cv-relation-tag is-sibling"
            aria-haspopup="dialog"
            aria-expanded={dialog === "siblings"}
            onClick={() => setDialog("siblings")}
          >
            <ExclamationTriangleIcon className="cv-relation-tag-icon" aria-hidden="true" />
            {t("case.siblings.tag", { count: siblings.length })}
          </button>
        ) : null}
      </div>

      <PMDialog
        open={Boolean(dialog)}
        onClose={() => setDialog(null)}
        closeOnOutsideClick
        title={dialogTitle}
        size="md"
        zIndex={3600}
      >
        {dialog === "out" ? <TransferOutBody to={to} t={t} /> : null}
        {dialog === "in" ? <TransferInBody from={from} t={t} /> : null}
        {dialog === "siblings" ? <SiblingOpenBody list={siblings} t={t} /> : null}
      </PMDialog>
    </>
  );
}

export default CaseHeaderRelationTags;
